import { ReactNode, useEffect, useState } from "react";

export interface ModalRow {
  k: string;
  v: string;
  tone?: "paid";
}

/* Confirmation modal, order-summary edition (Jerry 2026-07-28): the modal
   recaps everything the rider chose (bike, plan, contract, collateral, paid)
   and gates EVERY confirm behind an explicit agreement checkbox. Layout and
   copy mirror the claude.design mock — no new information, just consolidated. */
export interface ModalConfig {
  title: string;
  eyebrow?: string;
  rows?: ModalRow[];
  /* Formatted amount ("R$600") — rendered in the blue amount box. */
  amount?: string;
  amountLabel?: string;
  /* dd/mm/yyyy due date. With an amount it renders inside the amount box;
     without one it renders as a standalone due row under the summary. */
  dueLabel?: string;
  dueTitle?: string;
  amountRows?: { k: string; v: string }[];
  body?: ReactNode;
  danger?: string;
  checkboxLabel?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm?: () => void;
}

export function Modal({
  config,
  onClose,
}: {
  config: ModalConfig | null;
  onClose: () => void;
}) {
  const [checked, setChecked] = useState(false);
  useEffect(() => setChecked(false), [config]);

  if (!config) return null;
  const needsCheck = Boolean(config.checkboxLabel);
  return (
    <div className="lpc-modal" role="dialog" aria-modal="true">
      <div className="lpc-modal__box">
        <div className="lpc-modal__title">{config.title}</div>
        {config.eyebrow ? (
          <div className="lpc-modal__eyebrow">{config.eyebrow}</div>
        ) : null}
        {config.rows?.length ? (
          <div
            className="lpc-sum"
            data-testid="modal-summary"
            style={{ marginBottom: config.amount || config.dueLabel ? 0 : 18 }}
          >
            {config.rows.map((r) => (
              <div className="lpc-sum__row" key={r.k}>
                <span className="k">{r.k}</span>
                <span className={"v" + (r.tone === "paid" ? " is-paid" : "")}>
                  {r.v}
                </span>
              </div>
            ))}
          </div>
        ) : null}
        {config.amount ? (
          <div className="lpc-modal__amount" data-testid="confirm-amount">
            <div className="top">
              <span className="k">
                {config.amountLabel ?? "Você paga agora"}
              </span>
              <span className="v">{config.amount}</span>
            </div>
            {config.dueLabel ? (
              <div className="due">
                <span>{config.dueTitle ?? "Vencimento da entrada"}</span>
                <span>{config.dueLabel}</span>
              </div>
            ) : null}
            {(config.amountRows ?? []).map((r) => (
              <div className="due due--sep" key={r.k}>
                <span>{r.k}</span>
                <span>{r.v}</span>
              </div>
            ))}
          </div>
        ) : config.dueLabel ? (
          <div className="lpc-due">
            <span className="k">{config.dueTitle ?? "Data de vencimento"}</span>
            <span className="v">{config.dueLabel}</span>
          </div>
        ) : null}
        {config.body ? (
          <div className="lpc-modal__body" style={{ marginTop: 16 }}>
            {config.body}
          </div>
        ) : null}
        {config.danger ? (
          <div className="lpc-modal__danger">{config.danger}</div>
        ) : null}
        {needsCheck ? (
          <label
            className={"lpc-agree" + (checked ? " is-on" : "")}
            data-testid="modal-check"
          >
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            <span className="lpc-agree__box">
              <svg viewBox="0 0 24 24">
                <path d="M5 13l4 4L19 7" />
              </svg>
            </span>
            <span>{config.checkboxLabel}</span>
          </label>
        ) : null}
        <button
          className="btn btn--primary"
          data-testid="modal-confirm"
          disabled={needsCheck && !checked}
          onClick={() => {
            onClose();
            config.onConfirm?.();
          }}
        >
          {config.confirmLabel ?? "Confirmar"}
        </button>
        <button
          className="btn btn--ghost"
          data-testid="modal-cancel"
          onClick={onClose}
        >
          {config.cancelLabel ?? "Voltar"}
        </button>
      </div>
    </div>
  );
}
